import React, { memo, useState } from 'react';
import type { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { Icon } from 'venn-ui-kit';
import { type DetailedProxyMetadata } from 'venn-api';
import { assertNotNil, useEventCallback, useModal } from 'venn-utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import {
  BulkManageAction,
  type BulkManageCashRow,
  type BulkManageFundRow,
  type BulkManageOptionRow,
  type BulkManageRow,
  type BulkManageUpdateFn,
} from '../types';
import { DeleteProxiesConfirmationModal } from '../DeleteProxiesConfirmationModal';
import type { FundToBulkProxy } from '../../modals/pickers/types';
import { isBulkManageCashRow, isBulkManageOptionRow } from '../utils';

export type HoldingProxyCellRendererParams = {
  canEditProxies: boolean;
  onUpdate: BulkManageUpdateFn;
  openBulkHoldingsProxyModal: (node: IRowNode<BulkManageRow>) => void;
};

// TODO: adjust this component to HoldingsProxy requirements (hook up the BE) VENN-30065
export function HoldingProxyCellRenderer({
  node,
  canEditProxies,
  onUpdate,
  openBulkHoldingsProxyModal,
}: ICellRendererParams<BulkManageFundRow | BulkManageCashRow | BulkManageOptionRow> & HoldingProxyCellRendererParams) {
  // useEventCallback ensures that we never rerender the expensive proxy info section due to the functions or their dependencies changing.
  const launchBulkProxyModal = useEventCallback(() => openBulkHoldingsProxyModal(node));
  const onRemove = useEventCallback(async () => {
    await onUpdate(
      BulkManageAction.FUND_HOLDINGS_PROXY_MODIFIED,
      assertNotNil(node.data, 'Node data is required to remove proxy'),
    );
  });

  if (isBulkManageCashRow(node.data)) {
    return <ProxyUnavailableMessage tooltipContent={`Proxying is unavailable for ${node.data.name}.`} />;
  }

  if (isBulkManageOptionRow(node.data)) {
    return <ProxyUnavailableMessage tooltipContent="Proxying is unavailable for options." />;
  }

  if (node?.group || !node.data?.investment) return null;

  const isCompositeBenchmark = node.data.investment.assetType === 'BENCHMARK';
  if (isCompositeBenchmark) {
    return <ProxyUnavailableMessage tooltipContent="Proxying is unavailable for composite benchmarks." />;
  }

  const nodeData = node.data;
  const fundHoldingsProxyInfo = nodeData.fundHoldingsProxyInfo;
  if (fundHoldingsProxyInfo?.proxyId) {
    return (
      <FundProxyInfoSection
        investment={assertNotNil(nodeData.investment)}
        fundProxyInfo={fundHoldingsProxyInfo}
        canEditProxies={canEditProxies}
        onRemove={onRemove}
        launchBulkProxyModal={launchBulkProxyModal}
      />
    );
  }

  if (!canEditProxies) {
    return null;
  }

  if (node.data.secondary) {
    if (!node.data.isBenchmark) {
      return null;
    }
    return (
      <div className="flex gap-[6px] pr-[6px] items-center">
        <span className="text-venn-grey-80 grow shrink text-ellipsis overflow-clip whitespace-nowrap min-w-0">
          Set holdings proxy on benchmark
        </span>
        <Button onClick={launchBulkProxyModal} variant="ghost" size="icon" className="size-max hover:bg-transparent">
          <Icon className="text-venn-highlight-dark hover:text-venn-highlight-light" type="circle-plus" prefix="far" />
        </Button>
      </div>
    );
  }

  return checkBoxToSetHoldingsProxyMessage;
}

/** Component is memoized for performance; pass in only referentially stable data to prevent rerenders. */
const FundProxyInfoSection = memo(function _FundProxyInfoSection({
  investment,
  fundProxyInfo,
  canEditProxies,
  onRemove,
  launchBulkProxyModal,
}: {
  investment: FundToBulkProxy;
  fundProxyInfo: DetailedProxyMetadata;
  canEditProxies: boolean;
  onRemove: () => Promise<void>;
  launchBulkProxyModal: () => void;
}) {
  const investmentId = investment.id;

  const [isDeleteConfirmationOpen, openDeleteConfirmation, closeDeleteConfirmation] = useModal();
  const [isProxiesUpdating, setIsProxiesUpdating] = useState(false);

  const modal = isDeleteConfirmationOpen && (
    <DeleteProxiesConfirmationModal
      fundsWithProxy={[investment]}
      proxyDataByFund={{}}
      holdingsProxyDataByFund={{
        [investmentId]: fundProxyInfo,
      }}
      onCancel={closeDeleteConfirmation}
      onStartingUpdate={() => setIsProxiesUpdating(true)}
      onFinishedUpdate={() => {
        closeDeleteConfirmation();
        onRemove().finally(() => {
          setIsProxiesUpdating(false);
        });
      }}
    />
  );

  return (
    <div className="flex gap-[6px] pr-[6px]">
      {modal}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex grow shrink min-w-0 overflow-clip items-center gap-[3px]">
            <Icon type="arrows-left-right-to-line" className="text-xs text-venn-highlight-dark" />
            <div className="overflow-hidden whitespace-nowrap text-ellipsis text-venn-black">
              {fundProxyInfo.proxyName}
            </div>
          </div>
        </TooltipTrigger>

        <TooltipPortal>
          <TooltipContentWithArrow className="px-[9px]">
            {renderHoldingProxyInfo(fundProxyInfo.proxyName)}
          </TooltipContentWithArrow>
        </TooltipPortal>
      </Tooltip>

      {canEditProxies && (
        <div className="flex gap-[6px] items-center">
          <Button
            onClick={launchBulkProxyModal}
            variant="ghost"
            size="icon"
            className="size-max text-venn-highlight-dark hover:text-venn-highlight-light hover:bg-transparent"
            disabled={isProxiesUpdating}
          >
            <Icon type="edit" />
          </Button>
          <Button
            onClick={openDeleteConfirmation}
            variant="ghost"
            size="icon"
            className="size-max text-venn-grey-50 hover:text-venn-error hover:bg-transparent"
            disabled={isProxiesUpdating}
          >
            <Icon type="circle-x" />
          </Button>
        </div>
      )}
    </div>
  );
});

const ProxyUnavailableMessage = memo(function _ProxyUnavailableTooltip({
  tooltipContent,
}: {
  tooltipContent: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger className="flex items-center gap-[3px] h-full">
        <div className="text-venn-grey-80">Proxying Unavailable</div>{' '}
        <Icon type="circle-info" className="text-venn-grey-50" />
      </TooltipTrigger>

      <TooltipPortal>
        <TooltipContentWithArrow className="px-[9px]">{tooltipContent}</TooltipContentWithArrow>
      </TooltipPortal>
    </Tooltip>
  );
});

const checkBoxToSetHoldingsProxyMessage = <div className="text-venn-grey-50">Check box to set a proxy</div>;

const renderHoldingProxyInfo = (name?: string) => {
  return (
    <div className="text-white w-auto flex flex-col mr-[20px]:first-child">
      <div className="text-[10px] font-bold">Holdings Proxy Data:</div>
      <div className="text-[14px] text-venn-pale-gold py-[3px] pt-[3px] pb-[4px]">{name}</div>
    </div>
  );
};
